import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import router from '@/router';
import Paths from '@/constants/Paths';
import { CustomerProfileVm, OAuthCustomerProfileVm, OAuthProviderEnum } from '@/api';
import { jwtDecode } from 'jwt-decode';
import { useUserAccountStore } from '@/store/useUserAccountStore';
import { useCardApplicationStore } from '@/store/useCardApplicationStore';
import { useCustomerStore } from '@/store/useCustomerStore';
import { useShoppingCartStore } from '@/store/useShoppingCartStore';
import { useSystemConfigurationStore } from '@/store/useSystemConfigurationStore';
import { formatName } from '@/utils/customer';
import { differenceInMilliseconds } from 'date-fns';
import { loadPublicSystemConfiguration } from '@/utils/systemConfiguration';

interface JwtInterface {
  customerAccountId: string;
  iat: number;
  exp: number;
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const userAccountStore = useUserAccountStore();
    const systemConfigurationStore = useSystemConfigurationStore();
    const shoppingCartStore = useShoppingCartStore();
    const customerStore = useCustomerStore();
    const cardApplicationStore = useCardApplicationStore();

    const logged = ref(false);
    // User has JWT but doesn't have access to application or account
    // usefull for oauth login flows
    const partiallyLogged = ref(false);
    const token = ref('');
    const customerAccountId = ref('');
    const username = ref('');
    const hasPassword = ref(false);
    const tokenExpiresIn = ref<string | Date>('');
    const logoutWhenExpiredTimeout = ref<NodeJS.Timeout | null>(null);
    const oAuthProvider = ref<OAuthProviderEnum | null>(null);

    const loggedInAccountDisplayName = computed(() => {
      return customerStore.hasLoggedInAccountCustomer ? formatName(customerStore.registeredCustomer) : username.value;
    });

    const tokenExpired = computed(() => {
      return differenceInMilliseconds(tokenExpiresIn.value, new Date()) <= 0;
    });

    const partialLogin = (jwtToken: string) => {
      partiallyLogged.value = true;
      token.value = jwtToken;

      const decodedJWT: JwtInterface = jwtDecode(jwtToken);

      const actualDate = new Date(0);
      actualDate.setUTCSeconds(decodedJWT.exp);
      tokenExpiresIn.value = actualDate;

      const expirationDate = differenceInMilliseconds(actualDate, new Date());
      logoutWhenExpiredTimeout.value = setTimeout(() => {
        logout();
      }, expirationDate);
    };

    const login = (payload: CustomerProfileVm | OAuthCustomerProfileVm) => {
      cardApplicationStore.initCardApplication();
      userAccountStore.clearCardData();
      userAccountStore.clearSelectedCustomerData();

      logged.value = true;
      partiallyLogged.value = false;
      token.value = payload.token;
      customerAccountId.value = payload.customerAccountId ?? '';
      username.value = payload.username ?? '';
      hasPassword.value = 'hasPassword' in payload ? payload.hasPassword : false;
      oAuthProvider.value = 'oAuthProfile' in payload ? payload.oAuthProfile.provider : null;
      if (payload.customer) {
        customerStore.setCustomer(payload.customer);

        if (payload.customer.cartLifeTime) {
          shoppingCartStore.setExpirationTime(payload.customer.cartLifeTime);
          shoppingCartStore.setCartLifetimeTimer();
        }
      }

      const decodedJWT: JwtInterface = jwtDecode(payload.token);

      const actualDate = new Date(0);
      actualDate.setUTCSeconds(decodedJWT.exp);
      tokenExpiresIn.value = actualDate;

      const expirationDate = differenceInMilliseconds(actualDate, new Date());
      logoutWhenExpiredTimeout.value = setTimeout(() => {
        logout();
      }, expirationDate);
    };

    const partialLogout = () => {
      partiallyLogged.value = false;
      token.value = '';
      tokenExpiresIn.value = '';
      if (logoutWhenExpiredTimeout.value) {
        clearTimeout(logoutWhenExpiredTimeout.value);
      }
      router.push(Paths.LOGIN);
    };

    const logout = () => {
      const userAccountStore = useUserAccountStore();

      partiallyLogged.value = false;
      logged.value = false;
      token.value = '';
      customerAccountId.value = '';
      customerStore.clearLinkedCustomer();
      username.value = '';
      customerStore.clearCustomer();
      systemConfigurationStore.clearSystemConfiguration();
      tokenExpiresIn.value = '';
      if (logoutWhenExpiredTimeout.value) {
        clearTimeout(logoutWhenExpiredTimeout.value);
      }

      cardApplicationStore.initCardApplication();
      userAccountStore.clearCardData();
      userAccountStore.clearSelectedCustomerData();

      loadPublicSystemConfiguration();
      router.push(Paths.LOGIN);
    };

    return {
      customerAccountId,
      logged,
      partiallyLogged,
      token,
      loggedInAccountDisplayName,
      tokenExpired,
      tokenExpiresIn,
      logoutWhenExpiredTimeout,
      username,
      hasPassword,
      oAuthProvider,

      login,
      logout,
      partialLogin,
      partialLogout,
    };
  },
  { persist: true },
);
